<template>
  <v-container class="pa-4">
    <v-row>
      <v-col cols="12">
        <v-tabs
          v-model="tab"
          bg-color="white"
          color="black"
          align-tabs="start"
        >
          <v-tab value="list" v-if="showParameterList">
            <v-icon class="mr-2" size="small">mdi-format-list-bulleted</v-icon>
            List
          </v-tab>
          <v-tab v-if="isSmartPowerPlus" value="files">
            <v-icon class="mr-2" size="small"
              >mdi-file-document-multiple-outline</v-icon
            >
            Files
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <v-tabs-window v-model="tab">
      <!-- LIST -->
      <v-tabs-window-item value="list" v-if="showParameterList">
        <v-row class="mt-2">
          <v-col xs="auto" class="mr-0">
            <v-text-field
              v-model="search"
              prepend-icon="mdi-magnify"
              :label="$t('common.filters.filter')"
              variant="underlined"
              hide-details
              class="ma-0 pa-0"
            ></v-text-field>
          </v-col>
          <v-col cols="auto">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  @click="refreshParameters"
                  :loading="refreshLoading"
                  :disabled="refreshLoading"
                >
                  <v-icon>mdi-cached</v-icon>
                  <template v-slot:loader>
                    <span class="refreshLoader">
                      <v-icon>mdi-cached</v-icon>
                    </span>
                  </template>
                </v-btn>
              </template>
              {{ $t("common.actions.refresh") }}
            </v-tooltip>

            <v-tooltip location="top" v-if="canEditParameters">
              <template v-slot:activator="{ props }">
                <span v-bind="props">
                  <v-btn
                    icon
                    variant="text"
                    @click="pushStashedChanges"
                    :disabled="!hasStashedChanges"
                  >
                    <v-icon>mdi-content-save</v-icon>
                    <template v-slot:loader>
                      <span>
                        <v-icon>mdi-content-save</v-icon>
                      </span>
                    </template>
                  </v-btn>
                </span>
              </template>
              {{ $t("common.actions.applyChanges") }}
            </v-tooltip>
          </v-col>
        </v-row>

        <v-data-table
          class="mt-2"
          v-if="machineParameters"
          :headers="headers"
          :items="machineParameters"
          :loading="isLoading"
          :search="search"
          @click:row="rowClickHandler"
        >
          <template v-slot:[`item.state`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div class="d-flex justify-center">
                  <v-icon v-bind="props" :color="getColor(item.state)">{{
                    getIcon(item.state)
                  }}</v-icon>
                </div>
              </template>
              <span>{{ getToolTip(item.state) }}</span>
            </v-tooltip>
          </template>

          <template v-slot:[`item.rawValue`]="{ item }">
            <span class="mr-2">{{ item.rawValue }}</span>
            <span v-if="shouldHaveTooltip(item)">
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-icon size="small" v-bind="props">mdi-information</v-icon>
                </template>
                <div v-for="txt in getParameterText(item)" :key="txt">
                  {{ txt }}
                </div>
              </v-tooltip>
            </span>
          </template>
        </v-data-table>
      </v-tabs-window-item>

      <!-- PARAMETER FILES -->
      <v-tabs-window-item v-if="isSmartPowerPlus" value="files">
        <ParameterFiles :machine="machine" />
      </v-tabs-window-item>
    </v-tabs-window>
    <EditParameterDialog
      v-if="parameterDialog"
      show="true"
      :parameterDialog="parameterDialog"
    />
  </v-container>
</template>

<script>
import EditParameterDialog from "../../dialogs/EditParameterDialog"
import ParameterFiles from "../../components/machines/ParameterFiles"
import colors from "vuetify/lib/util/colors"

export default {
  name: "MachineParameters",
  props: ["machine"],
  components: { EditParameterDialog, ParameterFiles },

  data: () => ({
    tab: "list",
    parameters: [],
    search: "",
    parameterState: {
      DEFAULT: "default",
      APPLIED: "applied",
      PENDING: "pending",
      MODIFIED: "modified"
    },
    parameterDialog: null,

    refreshLoading: false
  }),
  computed: {
    headers() {
      return [
        {
          title: this.$t("machines.parameters.dataTable.headers.id"),
          align: "center",
          sortable: true,
          filterable: true,
          key: "parameterId",
          width: "5%"
        },
        {
          title: this.$t("machines.parameters.dataTable.headers.number"),
          align: "right",
          sortable: true,
          filterable: true,
          key: "oldParameter",
          width: "8%"
        },
        {
          title: this.$t("machines.parameters.dataTable.headers.category"),
          align: "left",
          sortable: true,
          filterable: true,
          key: "category"
        },
        {
          title: this.$t("machines.parameters.dataTable.headers.device"),
          align: "left",
          sortable: true,
          filterable: true,
          key: "device"
        },
        {
          title: this.$t("machines.parameters.dataTable.headers.description"),
          align: "start",
          sortable: true,
          filterable: true,
          key: "description"
        },
        {
          title: this.$t("machines.parameters.dataTable.headers.value"),
          align: "right",
          sortable: false,
          filterable: false,
          key: "rawValue"
        }
      ]
    },
    machineParameters() {
      const mergedParameters = [
        ...this.$store.getters.machineParameters,
        ...this.$store.getters.stashedChanges.filter(
          x => x.machineId == this.machine.serialNo
        )
      ]

      const uniqueById = [
        ...new Map(
          mergedParameters.map(item => [item.parameterId, item])
        ).values()
      ]
      return uniqueById
    },
    hasStashedChanges() {
      return this.$store.getters.stashedChanges.length > 0
    },
    isLoading() {
      return this.$store.getters.isLoadingParameters
    },
    showParameterList() {
      return (
        this.$store.getters.isAdmin ||
        this.$store.getters.isService ||
        this.$store.getters.isProduction
      )
    },
    canEditParameters() {
      // This is a feature toggle to hide the save button and to disable click handler on table
      // Should be mapped to this.$store.getters.canEditParameters
      return false
    },
    specialFuncsMetadata() {
      return this.$store.getters.specialFuncsMetadata
    },
    isSmartPowerPlus() {
      return this.machine.properties?.tags?.includes("smart_power_plus")
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    getToolTip(state) {
      switch (state) {
        case this.parameterState.MODIFIED:
          return this.$t("machines.messages.unsavedChanges")
        case this.parameterState.APPLIED:
          return this.$t("machines.messages.changesApplied")
        case this.parameterState.PENDING:
          return this.$t("machines.messages.applyChangesPending")
        default:
          return ""
      }
    },
    getColor(state) {
      switch (state) {
        case this.parameterState.MODIFIED:
          return colors.amber.base
        case this.parameterState.APPLIED:
          return colors.green.base
        case this.parameterState.PENDING:
          return colors.amber.base
        default:
          return ""
      }
    },
    getIcon(state) {
      switch (state) {
        case this.parameterState.MODIFIED:
          return "mdi-lead-pencil"
        case this.parameterState.APPLIED:
          return "mdi-check"
        case this.parameterState.PENDING:
          //return "mdi-lan-pending"
          return "mdi-clock-alert"
        default:
          return ""
      }
    },
    getParameterText(item) {
      if (item.parameterId === "P9") {
        // Special functions code is a bit-mask
        let mask = Number.parseInt(item.rawValue)
        let txts = []

        if (mask > 0) {
          this.specialFuncsMetadata.forEach(opt => {
            if (mask & opt.value) {
              txts.push(opt.description)
            }
          })
        } else {
          txts.push("None")
        }

        return txts
      } else if (item.parameterId === "P18" || item.parameterId === "P84") {
        // Service mode is a bit-mask
        let mask = Number.parseInt(item.rawValue)
        let txts = []

        if ((mask & 0x01) === 0x01) {
          txts.push("Active")
        } else {
          txts.push("Inactive")
        }

        return txts
      }
      return [item.rawValue]
    },
    shouldHaveTooltip(item) {
      return (
        item.parameterId === "P9" ||
        item.parameterId === "P18" ||
        item.parameterId === "P84"
      )
    },
    pushStashedChanges() {
      this.$store.dispatch("pushStashedChanges", {
        serialNo: this.machine.serialNo,
        tenantUid: this.machine.tenantUid
      })
    },
    fetchData() {
      this.$store.dispatch("fetchMachineParameters", {
        tenantUid: this.machine.tenantUid,
        serialNo: this.machine.serialNo,
        langCode: "en"
      })

      // Load meta data for special functions
      this.$store.dispatch("loadSpecialFuncsMetadata", "en")

      this.$store.dispatch("clearStashedChanges")
    },
    refreshParameters() {
      this.refreshLoading = true
      this.fetchData()
      setTimeout(() => {
        this.refreshLoading = false
      }, 1000)
    },
    rowClickHandler(event, { item: clickedItem }) {
      if (!this.canEditParameters) {
        return
      }
      let dialog = {
        item: { ...clickedItem },
        buttons:
          clickedItem.editMode !== "never"
            ? [
                {
                  label: this.$t("common.actions.save"),
                  action: () => {
                    this.$store.dispatch("updateStashedChanges", {
                      ...this.$data.parameterDialog.item,
                      machineId: this.machine.serialNo
                    })
                  }
                }
              ]
            : []
      }
      this.$data.parameterDialog = dialog
    }
  }
}
</script>
<style>
.stateCol {
  width: 130px;
}
.v-table__wrapper tr {
  cursor: pointer;
}
.v-table__wrapper th {
  cursor: default;
}
.refreshLoader {
  animation: loader 1s infinite;
  display: flex;
}
@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(-360deg);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(-360deg);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(-360deg);
  }
}
@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(-360deg);
  }
}
</style>
